import { useEffect, useRef } from 'react';
import { detectMediaType, getYouTubeEmbedUrl, getVimeoEmbedUrl, getLinkedInEmbedUrl } from '../lib/cloudinary';

// Loads a platform embed script once, then (re)processes any embeds on the page.
function loadEmbedScript(src, onReady) {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    if (existing.getAttribute('data-loaded') === 'true') onReady?.();
    else existing.addEventListener('load', () => onReady?.());
    return;
  }
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.onload = () => { s.setAttribute('data-loaded', 'true'); onReady?.(); };
  document.body.appendChild(s);
}

function processEmbed(embedCode) {
  const code = (embedCode || '').toLowerCase();
  if (code.includes('instagram-media') || code.includes('instagram.com')) {
    loadEmbedScript('https://www.instagram.com/embed.js', () => window.instgrm?.Embeds?.process());
  } else if (code.includes('tiktok-embed') || code.includes('tiktok.com')) {
    loadEmbedScript('https://www.tiktok.com/embed.js');
  } else if (code.includes('fb-post') || code.includes('fb-video') || code.includes('facebook.com')) {
    loadEmbedScript('https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v19.0', () => window.FB?.XFBML?.parse());
  }
}

// Styles for .mr-embed-frame and .mr-embed live in index.css for single injection.
function SocialEmbed({ embedCode }) {
  const ref = useRef(null);
  useEffect(() => {
    // Give the injected markup a tick to mount, then run the platform script.
    const t = setTimeout(() => processEmbed(embedCode), 50);
    return () => clearTimeout(t);
  }, [embedCode]);
  return (
    <div className="mr-embed-frame">
      <div className="mr-embed" ref={ref} dangerouslySetInnerHTML={{ __html: embedCode }} />
    </div>
  );
}

// Extract Instagram reel/post shortcode from URL
function getInstagramEmbedUrl(url) {
  const match = url.match(/instagram\.com\/(reel|p)\/([A-Za-z0-9_-]+)/);
  if (!match) return null;
  return `https://www.instagram.com/${match[1]}/${match[2]}/embed/`;
}

// Extract Facebook reel/video embed URL
function getFacebookEmbedUrl(url) {
  return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=500&mute=0`;
}

// Extract TikTok video ID from URL
function getTikTokEmbedUrl(url) {
  const match = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (!match) return null;
  return `https://www.tiktok.com/embed/v2/${match[1]}`;
}

export default function MediaEmbed({ url, embedCode, thumbnail, title, height = 200 }) {
  // If raw embed code is provided (Instagram, Facebook, TikTok), render + run its script
  if (embedCode) {
    return <SocialEmbed embedCode={embedCode} />;
  }

  if (!url && !thumbnail) return <PlaceholderThumb title={title} height={height} />;

  const type = url ? detectMediaType(url) : 'image';

  if (type === 'youtube') {
    const embedUrl = getYouTubeEmbedUrl(url);
    if (!embedUrl) return <PlaceholderThumb title={title} height={height} />;
    return (
      <iframe
        src={embedUrl}
        width="100%"
        height={height}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ display: 'block', borderRadius: '12px 12px 0 0' }}
        title={title}
      />
    );
  }

  if (type === 'vimeo') {
    const embedUrl = getVimeoEmbedUrl(url);
    if (!embedUrl) return <PlaceholderThumb title={title} height={height} />;
    return (
      <iframe
        src={embedUrl}
        width="100%"
        height={height}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        style={{ display: 'block', borderRadius: '12px 12px 0 0' }}
        title={title}
      />
    );
  }

  if (type === 'instagram') {
    const embedUrl = getInstagramEmbedUrl(url);
    if (!embedUrl) return <PlaceholderThumb title={title} height={height} />;
    return (
      <iframe
        src={embedUrl}
        width="100%"
        height={height}
        frameBorder="0"
        scrolling="no"
        allowTransparency="true"
        style={{ display: 'block', borderRadius: '12px 12px 0 0', minHeight: height }}
        title={title}
      />
    );
  }

  if (type === 'facebook') {
    const embedUrl = getFacebookEmbedUrl(url);
    return (
      <iframe
        src={embedUrl}
        width="100%"
        height={height}
        frameBorder="0"
        scrolling="no"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        style={{ display: 'block', borderRadius: '12px 12px 0 0' }}
        title={title}
      />
    );
  }

  if (type === 'tiktok') {
    const embedUrl = getTikTokEmbedUrl(url);
    if (!embedUrl) return <PlaceholderThumb title={title} height={height} />;
    return (
      <iframe
        src={embedUrl}
        width="100%"
        height={height}
        frameBorder="0"
        scrolling="no"
        allow="encrypted-media"
        allowFullScreen
        style={{ display: 'block', borderRadius: '12px 12px 0 0' }}
        title={title}
      />
    );
  }

  if (type === 'linkedin') {
    const embedUrl = getLinkedInEmbedUrl(url);
    if (!embedUrl) return <PlaceholderThumb title={title} height={height} />;
    return (
      <iframe
        src={embedUrl}
        width="100%"
        height={Math.max(height, 500)}
        frameBorder="0"
        scrolling="no"
        allow="encrypted-media"
        allowFullScreen
        style={{ display: 'block', borderRadius: '12px 12px 0 0', minHeight: Math.max(height, 500) }}
        title={title}
      />
    );
  }

  // Image (Cloudinary or any URL)
  if (url || thumbnail) {
    return (
      <img
        src={url || thumbnail}
        alt={title}
        style={{ width: '100%', height, objectFit: 'cover', objectPosition: 'top', display: 'block', borderRadius: '12px 12px 0 0' }}
      />
    );
  }

  return <PlaceholderThumb title={title} height={height} />;
}

function PlaceholderThumb({ title, height }) {
  return (
    <div style={{ width: '100%', height, background: 'linear-gradient(135deg, var(--blush), var(--lavender))', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: '12px 12px 0 0' }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(180,80,100,.4)" strokeWidth="1.5" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <path d="M21 15l-5-5L5 21"/>
      </svg>
      <span style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(180,80,100,.5)', textAlign: 'center', padding: '0 12px' }}>
        {title || 'Add thumbnail / embed'}
      </span>
    </div>
  );
}
